<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useConnectivity } from '@/composables/useConnectivity';

withDefaults(
  defineProps<{
    /** Hide the page-level title (card header supplies it). */
    embedded?: boolean;
  }>(),
  { embedded: false }
);

const { t } = useI18n();
const { isOnline } = useConnectivity();
const authStore = useAuthStore();
const route = useRoute();
const { signupLoading, signupErrors, signupSuccess } = storeToRefs(authStore);

const form = reactive({
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  introduction: '',
});

/**
 * Current route path + query, safe to use as redirect after verification.
 * Returns undefined if not a safe relative path (e.g. avoid "//").
 */
function getNextFromCurrentRoute(): string | undefined {
  const full = route.fullPath;
  if (!full || !full.startsWith('/') || full.includes('//')) return undefined;
  return full;
}

async function onSubmit(e: Event) {
  e.preventDefault();
  if (!isOnline.value) return;
  await authStore.signup({
    email: form.email,
    first_name: form.first_name,
    last_name: form.last_name,
    phone: form.phone || undefined,
    introduction: form.introduction || undefined,
    next: getNextFromCurrentRoute(),
  });
}
</script>

<template>
  <div class="signup-form" :class="{ 'signup-form--embedded': embedded }">
    <h2 v-if="!embedded" class="signup-form__title">{{ t('account.signupForm.title') }}</h2>
    <p v-if="signupSuccess" class="auth-feedback auth-feedback--success">
      {{ t('account.signupForm.successMessage') }}
    </p>
    <form v-else class="auth-form" @submit="onSubmit">
      <p v-if="signupErrors.generalError" class="auth-feedback auth-feedback--error" role="alert">
        {{ signupErrors.generalError }}
      </p>
      <div class="auth-field">
        <label for="signup-email" class="auth-label">{{ t('account.signupForm.emailLabel') }}</label>
        <input
          id="signup-email"
          v-model="form.email"
          type="email"
          name="email"
          autocomplete="email"
          required
          :disabled="signupLoading || !isOnline"
          :aria-invalid="(signupErrors.fieldErrors?.email?.length ?? 0) > 0"
          :aria-describedby="(signupErrors.fieldErrors?.email?.length ?? 0) ? 'signup-email-err' : undefined"
          class="auth-input"
          :placeholder="t('account.signupForm.emailPlaceholder')"
        />
        <p v-if="signupErrors.fieldErrors?.email?.length" id="signup-email-err" class="auth-field-error" role="alert">
          {{ signupErrors.fieldErrors.email[0] }}
        </p>
      </div>
      <div :class="embedded ? 'signup-form__pair' : 'signup-form__stack-plain'">
        <div class="auth-field">
          <label for="signup-first_name" class="auth-label">{{ t('account.signupForm.firstNameLabel') }}</label>
          <input
            id="signup-first_name"
            v-model="form.first_name"
            type="text"
            name="first_name"
            autocomplete="given-name"
            required
            :disabled="signupLoading || !isOnline"
            :aria-invalid="(signupErrors.fieldErrors?.first_name?.length ?? 0) > 0"
            :aria-describedby="(signupErrors.fieldErrors?.first_name?.length ?? 0) ? 'signup-first_name-err' : undefined"
            class="auth-input"
            :placeholder="t('account.signupForm.firstNamePlaceholder')"
          />
          <p v-if="signupErrors.fieldErrors?.first_name?.length" id="signup-first_name-err" class="auth-field-error" role="alert">
            {{ signupErrors.fieldErrors.first_name[0] }}
          </p>
        </div>
        <div class="auth-field">
          <label for="signup-last_name" class="auth-label">{{ t('account.signupForm.lastNameLabel') }}</label>
          <input
            id="signup-last_name"
            v-model="form.last_name"
            type="text"
            name="last_name"
            autocomplete="family-name"
            required
            :disabled="signupLoading || !isOnline"
            :aria-invalid="(signupErrors.fieldErrors?.last_name?.length ?? 0) > 0"
            :aria-describedby="(signupErrors.fieldErrors?.last_name?.length ?? 0) ? 'signup-last_name-err' : undefined"
            class="auth-input"
            :placeholder="t('account.signupForm.lastNamePlaceholder')"
          />
          <p v-if="signupErrors.fieldErrors?.last_name?.length" id="signup-last_name-err" class="auth-field-error" role="alert">
            {{ signupErrors.fieldErrors.last_name[0] }}
          </p>
        </div>
      </div>
      <div class="auth-field">
        <label for="signup-phone" class="auth-label">{{ t('account.signupForm.phoneLabel') }}</label>
        <input
          id="signup-phone"
          v-model="form.phone"
          type="tel"
          name="phone"
          autocomplete="tel"
          :disabled="signupLoading || !isOnline"
          :aria-invalid="(signupErrors.fieldErrors?.phone?.length ?? 0) > 0"
          :aria-describedby="(signupErrors.fieldErrors?.phone?.length ?? 0) ? 'signup-phone-err' : undefined"
          class="auth-input"
          :placeholder="t('account.signupForm.phonePlaceholder')"
        />
        <p v-if="signupErrors.fieldErrors?.phone?.length" id="signup-phone-err" class="auth-field-error" role="alert">
          {{ signupErrors.fieldErrors.phone[0] }}
        </p>
      </div>
      <div class="auth-field">
        <label for="signup-introduction" class="auth-label">
          {{ t('account.signupForm.introductionLabel') }}
          <span class="signup-form__optional">({{ t('account.signupForm.optional') }})</span>
        </label>
        <textarea
          id="signup-introduction"
          v-model="form.introduction"
          name="introduction"
          rows="4"
          :disabled="signupLoading || !isOnline"
          :aria-invalid="(signupErrors.fieldErrors?.introduction?.length ?? 0) > 0"
          :aria-describedby="(signupErrors.fieldErrors?.introduction?.length ?? 0) ? 'signup-introduction-err' : undefined"
          class="auth-textarea"
          :placeholder="t('account.signupForm.introductionPlaceholder')"
        />
        <p v-if="signupErrors.fieldErrors?.introduction?.length" id="signup-introduction-err" class="auth-field-error" role="alert">
          {{ signupErrors.fieldErrors.introduction[0] }}
        </p>
      </div>
      <div :class="embedded ? 'signup-form__cta-row' : 'auth-actions'">
        <router-link
          v-if="embedded"
          class="auth-btn auth-btn--outline signup-form__cancel"
          to="/"
        >
          {{ t('account.authCard.cancel') }}
        </router-link>
        <button
          type="submit"
          :class="embedded ? 'auth-btn auth-btn--solid signup-form__submit' : 'auth-btn auth-btn--primary'"
          :disabled="signupLoading || !isOnline"
        >
          {{
            signupLoading
              ? t('account.authCard.signingUp')
              : embedded
                ? t('account.authCard.createAccount')
                : t('account.signupForm.submit')
          }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.signup-form {
  width: 100%;
}

.signup-form__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.signup-form__stack-plain {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.signup-form__pair {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 480px) {
  .signup-form--embedded .signup-form__pair {
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }
}

.signup-form--embedded .auth-input,
.signup-form--embedded .auth-textarea {
  border-radius: 6px;
  background: var(--color-surface);
}

.signup-form__cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.signup-form__cancel {
  text-decoration: none;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-radius: 2px;
}

.signup-form__submit {
  flex: 1 1 10rem;
  min-height: 2.875rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-radius: 2px;
}

.signup-form__optional {
  font-weight: 400;
  font-size: 0.875em;
  color: var(--color-text-muted);
}
</style>
